import { cellValue, stageLabel } from './demandListColumns.js';
import { canArchiveMore } from './archiveStorage.js';
import { retainEvidenceForMatters } from './evidenceVault.js';
import { applyMatterNotes } from './recordNotes.js';

const ARCHIVED_KEY = 'demandjet.archivedMatters';
const LEGACY_ARCHIVED_KEY = 'demandjet.archivedMatterIds';
const DELETED_KEY = 'demandjet.deletedMatters';
const ACCOUNTING_SYNC_KEY = 'demandjet.accountingSyncByMatter';

function readJson(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
}

function writeJson(key, value) {
  localStorage.setItem(key, JSON.stringify(value));
}

/** Map of matterId → { archivedAt } */
export function loadArchivedMap() {
  const map = readJson(ARCHIVED_KEY, null);
  if (map && typeof map === 'object' && !Array.isArray(map)) {
    return { ...map };
  }

  const legacy = readJson(LEGACY_ARCHIVED_KEY, []);
  const migrated = {};
  for (const id of Array.isArray(legacy) ? legacy : []) {
    if (id == null || id === '') continue;
    migrated[String(id)] = { archivedAt: null };
  }
  if (Object.keys(migrated).length) {
    writeJson(ARCHIVED_KEY, migrated);
  }
  return migrated;
}

export function saveArchivedMap(map) {
  const next = map && typeof map === 'object' ? map : {};
  writeJson(ARCHIVED_KEY, next);
  return next;
}

export function loadArchivedMatterIds() {
  return new Set(Object.keys(loadArchivedMap()));
}

export function saveArchivedMatterIds(ids) {
  const existing = loadArchivedMap();
  const next = {};
  const now = new Date().toISOString();
  for (const id of ids || []) {
    const key = String(id);
    next[key] = existing[key] || { archivedAt: now };
  }
  saveArchivedMap(next);
  return new Set(Object.keys(next));
}

/** Map of matterId → { deletedAt } for archived demands removed from the lists. */
export function loadDeletedMatterMap() {
  const map = readJson(DELETED_KEY, {});
  return map && typeof map === 'object' && !Array.isArray(map) ? { ...map } : {};
}

export function saveDeletedMatterMap(map) {
  const next = map && typeof map === 'object' ? map : {};
  writeJson(DELETED_KEY, next);
  return next;
}

export function loadAccountingSyncMap() {
  const map = readJson(ACCOUNTING_SYNC_KEY, {});
  return map && typeof map === 'object' ? map : {};
}

export function saveAccountingSyncMap(map) {
  writeJson(ACCOUNTING_SYNC_KEY, map || {});
  return map || {};
}

export function matterIsDeleted(matter) {
  const flag = matter?.Deleted;
  return flag === true || flag === 1 || flag === '1';
}

/** Apply archived + accounting-sync flags from local storage onto matter rows. */
export function applyLocalMatterFlags(matters = []) {
  const archived = loadArchivedMap();
  const deleted = loadDeletedMatterMap();
  const syncMap = loadAccountingSyncMap();
  const withFlags = (matters || []).map((matter) => {
    const id = String(matter?.ROWID || '');
    const isDeleted = Boolean(id && (deleted[id] || matterIsDeleted(matter)));
    const archivedMeta = archived[id];
    const isArchived = !isDeleted && (Boolean(matter.Archived) || Boolean(archivedMeta));
    return {
      ...matter,
      Deleted: isDeleted,
      Deleted_At: isDeleted ? matter.Deleted_At || deleted[id]?.deletedAt || null : null,
      Archived: isArchived,
      Archived_At: isArchived
        ? matter.Archived_At || archivedMeta?.archivedAt || null
        : null,
      Accounting_Synced_At: matter.Accounting_Synced_At || syncMap[id]?.syncedAt || null,
      Accounting_Sync_Note: matter.Accounting_Sync_Note || syncMap[id]?.note || null,
    };
  });
  return applyMatterNotes(withFlags);
}

export function activeMatters(matters = []) {
  return (matters || []).filter((matter) => !matter.Archived && !matterIsDeleted(matter));
}

export function archivedMatters(matters = []) {
  return (matters || [])
    .filter((matter) => matter.Archived && !matterIsDeleted(matter))
    .sort((a, b) => String(b.Archived_At || '').localeCompare(String(a.Archived_At || '')));
}

function csvEscape(value) {
  const text = String(value ?? '');
  if (/[",\n]/.test(text)) return `"${text.replace(/"/g, '""')}"`;
  return text;
}

const EXPORT_COLUMNS = [
  { id: 'debtorName', label: 'Debtor' },
  { id: 'company', label: 'Company' },
  { id: 'demandId', label: 'Demand ID' },
  { id: 'amount', label: 'Amount' },
  { id: 'stage', label: 'Status' },
  { id: 'attorney', label: 'Attorney partner' },
  { id: 'email', label: 'Email' },
  { id: 'state', label: 'State' },
  { id: 'city', label: 'City' },
  { id: 'postal', label: 'Postal code' },
  { id: 'invoiceId', label: 'Invoice ID' },
  { id: 'updatedAt', label: 'Last activity' },
];

export function mattersToCsv(matters = []) {
  const rows = matters || [];
  const header = EXPORT_COLUMNS.map((col) => csvEscape(col.label)).join(',');
  const lines = rows.map((matter) =>
    EXPORT_COLUMNS.map((col) => csvEscape(cellValue(matter, col.id))).join(',')
  );
  return [header, ...lines].join('\n');
}

export function exportMattersCsv(matters = [], filename) {
  const rows = matters || [];
  const csv = mattersToCsv(rows);
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  const stamp = new Date().toISOString().slice(0, 10);
  anchor.href = url;
  anchor.download = filename || `demandjet-demands-${stamp}.csv`;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
  return rows.length;
}

export function syncMattersToAccounting(matters = []) {
  const syncMap = loadAccountingSyncMap();
  const now = new Date().toISOString();
  const synced = [];
  const skipped = [];

  for (const matter of matters || []) {
    const id = String(matter?.ROWID || '');
    if (!id) continue;
    const invoiceId = matter.External_Invoice_ID || matter.Invoice_ID;
    if (!invoiceId) {
      skipped.push({ matter, reason: 'No invoice ID' });
      continue;
    }
    const note =
      matter.Status === 'PAID' || matter.Status === 'PAID_BEFORE_ASSIGNMENT'
        ? `Marked paid in accounting for invoice ${invoiceId}`
        : `Synced open invoice ${invoiceId} (${stageLabel(matter.Status)})`;
    syncMap[id] = {
      ...(syncMap[id] || {}),
      syncedAt: now,
      note,
      invoiceId: String(invoiceId),
    };
    synced.push({
      ...matter,
      Accounting_Synced_At: now,
      Accounting_Sync_Note: note,
      Updated_At: now,
    });
  }

  saveAccountingSyncMap(syncMap);
  return { synced, skipped, syncedAt: now };
}

/**
 * Archive selected matters. Archive capacity is unlimited on every demand plan.
 * @returns {{ updated: object[], blocked: object[], reason: string|null, usage: object|null, remaining: number|null }}
 */
export function archiveMatters(matters = [], { billing = {}, currentArchivedCount } = {}) {
  const map = loadArchivedMap();
  const used =
    currentArchivedCount == null ? Object.keys(map).length : Number(currentArchivedCount) || 0;
  const candidates = [];
  for (const matter of matters || []) {
    const id = String(matter?.ROWID || '');
    if (!id) continue;
    if (matter.Archived || map[id]) continue;
    candidates.push(matter);
  }

  const gate = canArchiveMore({
    used,
    addCount: candidates.length,
    billing,
  });
  if (!gate.ok) {
    return {
      updated: [],
      blocked: candidates,
      reason: 'LIMIT',
      usage: gate.usage,
      remaining: gate.remaining,
    };
  }

  const now = new Date().toISOString();
  const updated = [];
  for (const matter of candidates) {
    const id = String(matter.ROWID);
    map[id] = { archivedAt: now };
    updated.push({
      ...matter,
      Archived: true,
      Archived_At: now,
      Updated_At: now,
    });
  }
  saveArchivedMap(map);
  return {
    updated,
    blocked: [],
    reason: null,
    usage: gate.usage,
    remaining: gate.remaining,
  };
}

/**
 * Permanently remove archived demands from the portal lists.
 * They stay hidden after a refresh, including rows the server still returns.
 * @returns {object[]}
 */
export function permanentlyDeleteMatters(matters = []) {
  retainEvidenceForMatters(matters);
  const archived = loadArchivedMap();
  const deleted = loadDeletedMatterMap();
  const now = new Date().toISOString();
  const updated = [];
  for (const matter of matters || []) {
    const id = String(matter?.ROWID || '');
    if (!id) continue;
    delete archived[id];
    deleted[id] = { deletedAt: now };
    updated.push({
      ...matter,
      Archived: false,
      Archived_At: null,
      Deleted: true,
      Deleted_At: now,
      Updated_At: now,
      External_Invoice_ID: null,
      Invoice_ID: null,
      externalInvoiceId: null,
    });
  }
  saveArchivedMap(archived);
  saveDeletedMatterMap(deleted);
  return updated;
}

export function unarchiveMatters(matters = []) {
  const map = loadArchivedMap();
  const updated = [];
  for (const matter of matters || []) {
    const id = String(matter?.ROWID || '');
    if (!id) continue;
    delete map[id];
    updated.push({
      ...matter,
      Archived: false,
      Archived_At: null,
      Updated_At: new Date().toISOString(),
    });
  }
  saveArchivedMap(map);
  return updated;
}

export function selectedMattersFromIds(matters = [], selectedIds) {
  const ids = selectedIds instanceof Set ? selectedIds : new Set(selectedIds || []);
  return (matters || []).filter((matter) => ids.has(String(matter.ROWID)));
}
